import { showAvgRating } from "./posts";
import { renderLevelDistribution } from "./generics";

// Odpowiednik posts.ts, dla modułu "Przedmioty" (ocenianie samych kursów, nie wykładowców) -
// osobny plik, nie rozszerzenie posts.ts, bo Subject/Post mają inny kształt (kategorie zamiast
// nazwiska/wydziału) i inny zestaw dopuszczalnych sortowań.

export interface SubjectCategory {
    id: number;
    name: string;
}

export type SubjectSemester = "Z" | "L" | "";

// "id" to id wiersza SubjectStudyYear (do DELETE /api/subject-study-years/<id>/), NIE sam numer
// roku - patrz SubjectStudyYearLinkSerializer w pages/api/serializers.py.
export interface SubjectStudyYearLink {
    id: number;
    year_number: number;
    // Obieralny na TYM roku (np. Grupa K na WPiA - wybiera się 10/15 z listy), w odróżnieniu od
    // sztywno obowiązkowego przedmiotu - patrz SubjectStudyYear.is_elective w models.py.
    // Read-only (ustawiane tylko z panelu admina).
    is_elective: boolean;
}

// Kształt Post-a osadzony w chipie "Prowadzący" - patrz SubjectTeacherPostSerializer. Statystyki
// (gwiazdki/opinie/poleca/łatwość zajęć) - na wyraźną prośbę, żeby dało się je pokazać obok
// nazwiska na liście prowadzących danego przedmiotu (patrz buildTeachersEditor w
// subjectTeacherLinks.ts) bez osobnego zapytania per wykładowca.
export interface SubjectTeacherPost {
    id: number; name: string; surname: string; slug: string;
    avg_rating: number; number_of_comments: number; would_take_again_percent: number | null;
    avg_class_difficulty: number | null;
}

// "id" to id wiersza SubjectTeacher (do DELETE /api/subject-teachers/<id>/), NIE id posta -
// patrz SubjectTeacherSerializer w pages/api/serializers.py.
export interface SubjectTeacherLink {
    id: number;
    post: SubjectTeacherPost;
}

export interface Subject {
    id: number; name: string; slug: string; status: string;
    categories: SubjectCategory[];
    semester: SubjectSemester;
    study_years: SubjectStudyYearLink[];
    // Zawsze obecne (katalog I strona szczegółów, patrz SubjectCatalogSerializer w
    // pages/api/serializers.py) - w odróżnieniu od rating_distribution (tylko strona szczegółów),
    // bo widok "wybierz rok" (subjectYearDirectory.ts) pokazuje prowadzących wprost na liście
    // katalogu, bez przechodzenia na stronę każdego przedmiotu z osobna.
    teachers: SubjectTeacherLink[];
    avg_rating: number; avg_difficulty: number; number_of_comments: number;
    would_take_again_percent: number | null;
}

// Etykiety skali trudności (osobna od "rating" - jakość przedmiotu, ta mówi jak trudny jest, nie
// czy jest dobry) - własna ikona (🔥, nie gwiazdki ★), żeby wizualnie NIE mylić się z oceną.
export const DIFFICULTY_LABELS: { [key: number]: string } = {
    1: "Trywialny",
    2: "Łatwy",
    3: "Średni",
    4: "Trudny",
    5: "Piekielny",
};

const DIFFICULTY_ICON = "\u{1F525}"; // 🔥

// Odpowiednik showStars (generics.ts) - pięć ikon, wypełnione do wartości oceny. Używane w
// widoku pojedynczej opinii (obok gwiazdek) i w formularzu (patrz renderDifficultyRating w
// subjectComments.ts, który reużywa te same klasy CSS).
export function showDifficulty(difficulty: number): HTMLElement {
    const container = document.createElement("div");
    container.className = "difficulty-rating";
    for (let i = 1; i <= 5; i++) {
        const icon = document.createElement("span");
        icon.className = "difficulty-icon";
        icon.textContent = DIFFICULTY_ICON;
        if (i <= difficulty) icon.classList.add("active");
        container.appendChild(icon);
    }
    return container;
}

// Odpowiednik showPartialStars (posts.ts) dla trudności - ŚREDNIA (nie pojedyncza opinia, ta
// zawsze jest całkowita 1-5 - patrz showDifficulty wyżej) pokazana ciągłym wypełnieniem co 1/10,
// nie zaokrągloną liczbą całych płomieni (zgłoszenie: "2.5 to 2.5 płomyka, nie 2 lub 3"). Dwie
// nałożone kopie tego samego emoji zamiast jednej przebarwianej (jak .star-partial-bg/fg) -
// 🔥 renderuje się w pełnym kolorze niezależnie od CSS `color` (patrz notka przy .difficulty-icon
// w style.css), więc "wygaszoną" kopię w tle odróżnia opacity+grayscale (te same reguły co
// .difficulty-icon/.difficulty-icon.active), nie inny kolor.
export function showPartialDifficulty(avgDifficulty: number): HTMLElement {
    const container = document.createElement("div");
    container.className = "difficulty-rating difficulty-rating-partial";

    for (let i = 1; i <= 5; i++) {
        const rawFraction = Math.max(0, Math.min(1, avgDifficulty - (i - 1)));
        const tenthFraction = Math.round(rawFraction * 10) / 10;

        const iconWrap = document.createElement("span");
        iconWrap.className = "difficulty-icon-partial";

        const iconBg = document.createElement("span");
        iconBg.className = "difficulty-icon difficulty-icon-partial-bg";
        iconBg.textContent = DIFFICULTY_ICON;

        const iconFg = document.createElement("span");
        iconFg.className = "difficulty-icon active difficulty-icon-partial-fg";
        iconFg.style.width = `${tenthFraction * 100}%`;
        iconFg.textContent = DIFFICULTY_ICON;

        iconWrap.append(iconBg, iconFg);
        container.appendChild(iconWrap);
    }

    return container;
}

// Zwięzła odznaka średniej trudności - jedna ikona + liczba (jak renderWouldTakeAgainBadge w
// posts.ts), na karcie listy/w podsumowaniu strony przedmiotu - pięć powtórzonych ikon zajmowałoby
// tam za dużo miejsca obok już i tak obecnych gwiazdek oceny.
export function showAvgDifficulty(avgDifficulty: number): HTMLElement {
    const badge = document.createElement("span");
    badge.className = "difficulty-badge";
    badge.textContent = `${DIFFICULTY_ICON} ${avgDifficulty.toFixed(1)}`;
    badge.title = "Średnia zgłoszona trudność przedmiotu";
    return badge;
}

// Odpowiednik renderStarRating (comments.ts), dla trudności - te same mechanizmy (podświetlanie
// "w locie" na hover, etykieta pod spodem, dostęp z klawiatury), ale przyciski z ikoną zamiast
// ★ i własnym zestawem etykiet. Mieszka tu (nie w subjectComments.ts, gdzie było pierwotnie) -
// potrzebne też w comments.ts (formularz opinii o wykładowcy, "Łatwość zaliczenia"), a
// subjectComments.ts już importuje Z comments.ts (renderStarRating i in.), więc odwrotny import
// stąd stworzyłby cykl - subjects.ts nie importuje żadnego z tych dwóch modułów.
// icon/labels/ariaLabel są parametryzowane (domyślnie DIFFICULTY_ICON/DIFFICULTY_LABELS, dla
// trudności przedmiotu) - comments.ts wywołuje z INNYM zestawem (łatwość zaliczenia zajęć/
// egzaminu, kierunek skali odwrotny - 5 = dobrze, jak gwiazdki), żeby nie mylić dwóch różnych
// skal tą samą ikoną/etykietami (patrz EASE_OF_PASSING_LABELS w comments.ts).
export function renderDifficultyRating(
    onChange: (value: number) => void,
    icon: string = DIFFICULTY_ICON,
    labels: { [key: number]: string } = DIFFICULTY_LABELS,
    ariaLabel: string = "Ocena trudności",
): { element: HTMLElement, reset: () => void } {
    const wrapper = document.createElement("div");
    wrapper.className = "difficulty-rating-widget";

    const container = document.createElement("div");
    container.className = "difficulty-rating";
    container.setAttribute("role", "radiogroup");
    container.setAttribute("aria-label", ariaLabel);

    const hoverLabel = document.createElement("span");
    hoverLabel.className = "difficulty-rating-hover-label";

    let currentValue = 0;
    const buttons: HTMLButtonElement[] = [];

    const highlight = (count: number) => {
        buttons.forEach((b, index) => b.classList.toggle("active", index < count));
    };
    const updateHoverLabel = (value: number) => {
        hoverLabel.textContent = value > 0 ? labels[value] : "";
    };

    let hideTimeout: ReturnType<typeof setTimeout> | undefined;
    const clearHideTimeout = () => {
        if (hideTimeout !== undefined) { clearTimeout(hideTimeout); hideTimeout = undefined; }
    };
    const showLabelBriefly = (value: number) => {
        clearHideTimeout();
        updateHoverLabel(value);
        hideTimeout = setTimeout(() => { hideTimeout = undefined; updateHoverLabel(0); }, 1200);
    };

    for (let i = 1; i <= 5; i++) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "difficulty-btn";
        btn.textContent = icon;
        btn.dataset.value = String(i);
        btn.setAttribute("role", "radio");
        btn.setAttribute("aria-checked", "false");
        btn.setAttribute("aria-label", `${i} - ${labels[i]}`);

        btn.addEventListener("mouseenter", () => {
            clearHideTimeout();
            highlight(i);
            updateHoverLabel(i);
        });
        btn.addEventListener("click", () => {
            currentValue = i;
            highlight(i);
            showLabelBriefly(i);
            buttons.forEach((b, index) => b.setAttribute("aria-checked", String(index === i - 1)));
            onChange(currentValue);
        });

        buttons.push(btn);
        container.appendChild(btn);
    }

    container.addEventListener("mouseleave", () => {
        highlight(currentValue);
        if (hideTimeout === undefined) updateHoverLabel(0);
    });

    const reset = () => {
        currentValue = 0;
        onChange(0);
        clearHideTimeout();
        buttons.forEach(b => { b.classList.remove("active"); b.setAttribute("aria-checked", "false"); });
        updateHoverLabel(0);
    };

    wrapper.append(container, hoverLabel);
    return { element: wrapper, reset };
}

// Odpowiednik renderRatingDistribution (posts.ts), dla trudności (1=Trywialny..5=Piekielny)
// zamiast oceny gwiazdkowej - te same klasy CSS (.rating-distribution*, bary/kolory identyczne,
// tylko treść etykiet inna), więc bez osobnego bloku stylów. Używane na zakładce "Egzamin" na
// stronie wykładowcy (patrz post_detail.ts) - na razie jedyne miejsce z sensownym "rozkładem"
// trudności (koła naukowe/przedmioty oceniają trudność jako pojedynczą liczbę, bez rozkładu).
export function renderDifficultyDistribution(
    distribution: { [key: string]: number },
    options?: { showLabels?: boolean }
): HTMLElement {
    const el = renderLevelDistribution(distribution, DIFFICULTY_LABELS, options);
    el.classList.add("difficulty-distribution");
    return el;
}

export const SEMESTER_LABELS: { [key in Exclude<SubjectSemester, "">]: string } = {
    Z: "Sem. zimowy",
    L: "Sem. letni",
};

export type SubjectSortField = "most_comments" | "name" | "rating";

export function sortSubjects(subjects: Subject[], by: SubjectSortField): Subject[] {
    const sorted = [...subjects];
    sorted.sort((a, b) => {
        switch (by) {
            case "name":
                return a.name.localeCompare(b.name);
            case "rating": {
                const diff = b.avg_rating - a.avg_rating;
                return diff !== 0 ? diff : b.number_of_comments - a.number_of_comments;
            }
            case "most_comments":
                return b.number_of_comments - a.number_of_comments;
            default:
                return 0;
        }
    });
    return sorted;
}

export function renderSubject(subject: Subject, universitySlug: string, facultySlug: string): HTMLElement {
    const card = document.createElement("a");
    card.className = "comment-card";
    card.dataset.id = String(subject.id);
    card.href = `/${universitySlug}/${facultySlug}/przedmioty/${subject.slug}/`;

    const heading = document.createElement("h2");
    heading.textContent = subject.name;
    card.append(heading);

    // Kategorie (patrz .subject-card-categories) celowo NIE pokazujemy na karcie listy - tylko
    // na stronie samego przedmiotu (subject_detail.html, renderowane wprost przez Django).

    const meta = document.createElement("div");
    meta.className = "comment-card-meta";
    if (subject.number_of_comments > 0) {
        meta.append(
            showAvgRating(subject.avg_rating, subject.number_of_comments, subject.would_take_again_percent),
            showAvgDifficulty(subject.avg_difficulty),
        );
    } else {
        const noReviews = document.createElement("span");
        noReviews.className = "comment-card-no-reviews";
        noReviews.textContent = "Brak opinii";
        meta.append(noReviews);
    }
    card.append(meta);

    return card;
}
